import { useState } from 'react';
import { FUNNEL_STAGES, LoiDocumentDialog, loiDocumentContentPath } from '@dataroom/shared';
import type { BuyerAccessItem, BuyerEngagementSummary, LoiReviewItem } from '@dataroom/shared';
import {
  useAccessGrants,
  useAddBuyerTeamMember,
  useBuyerTeam,
  useDecideTeamRequest,
  useTeamRequests,
  useRemoveBuyerTeamMember,
  useSetTeamRoles,
  useAdvanceFunnel,
  useBuyerEngagement,
  useDecideLoi,
  useEndExclusivity,
  useExtendExclusivity,
  useRevokeGrant,
  useRoomExclusivity,
  useSetExclusivitySettings,
  useRollbackFunnel,
  useRoomBuyerEngagement,
  useRoomBuyers,
  useRoomLois,
  useSetRoomSellerLoiPermission,
  useUpdateGrantExpiry,
} from '@/api/hooks';
import { BuyerDetailModal } from '@/components/BuyerDetailModal';
import { BuyerTeamSection } from '@/components/BuyerTeamSection';
import { TeamRequestsCard } from '@/components/TeamRequestsCard';
import { BuyerEngagementCard } from '@/components/BuyerEngagementCard';
import { BuyerPipelineCard } from '@/components/BuyerPipelineCard';
import { CimAccessCard } from '@/components/CimAccessCard';
import { LoiManagementCard } from '@/components/LoiManagementCard';
import { apiMessage, plainError } from '@/lib/errors';
import { useRoomContext } from './context';

/** A buyer's access item from their activity row (a buyer not yet in the funnel). */
function buyerFromEngagement(b: BuyerEngagementSummary | undefined): BuyerAccessItem | null {
  if (!b) return null;
  const reached = (stage: BuyerAccessItem['stage']) =>
    FUNNEL_STAGES.indexOf(b.stage) >= FUNNEL_STAGES.indexOf(stage);
  return {
    actorId: b.actorId,
    actorEmail: b.actorEmail,
    stage: b.stage,
    ndaSigned: reached('nda_signed'),
    // The server's rule (access/authorization.ts isCimUnlocked): the NDA unlocks the CIM tier.
    cimUnlocked: reached('nda_signed'),
    allFilesUnlocked: reached('due_diligence'),
    accessExpiresAt: null,
  };
}

/**
 * The buyer pipeline: PRD-0004 §3e CIM access (each row opens the per-buyer
 * detail — advance / roll back stage, edit expiry, revoke) and §3g/§3h LOI
 * management (ADR-0029: open each uploaded signed LOI, record accept / counter /
 * reject, exclusivity / closing dates, plus the seller-may-respond toggle), and
 * ADR-0038 buyer activity (a side-by-side table; each row opens the same
 * per-buyer detail, which carries that buyer's Activity).
 */
export function RoomBuyersPage() {
  const { roomId: id, room } = useRoomContext();
  const buyers = useRoomBuyers(id);
  const grants = useAccessGrants(id);
  const revoke = useRevokeGrant(id);
  const rollback = useRollbackFunnel(id);
  const updateExpiry = useUpdateGrantExpiry(id);
  const advance = useAdvanceFunnel(id);
  // Track the selected buyer by id (not a snapshot) so the modal reflects the
  // buyer's live stage after an advance/roll-back refetch.
  const [selectedBuyerId, setSelectedBuyerId] = useState<string | null>(null);
  const engagement = useRoomBuyerEngagement(id);
  const buyerEngagement = useBuyerEngagement(id, selectedBuyerId);

  const lois = useRoomLois(id);
  const decide = useDecideLoi(id);
  // The LOI whose signed document is open in the read-only viewer.
  const [viewingLoi, setViewingLoi] = useState<LoiReviewItem | null>(null);
  const setSellerLoiPermission = useSetRoomSellerLoiPermission(id);
  // ADR-0046 enforced exclusivity.
  const exclusivity = useRoomExclusivity(id);
  const extend = useExtendExclusivity(id);
  const endExclusivity = useEndExclusivity(id);
  const clockSettings = useSetExclusivitySettings(id);
  // The selected buyer's named team (migration 0070).
  const buyerTeam = useBuyerTeam(id, selectedBuyerId);
  const addTeamMember = useAddBuyerTeamMember(id);
  const removeTeamMember = useRemoveBuyerTeamMember(id);
  const setTeamRoles = useSetTeamRoles(id);
  // Buyers' waiting team asks (migration 0075): approve or decline.
  const teamRequests = useTeamRequests(id);
  const decideTeamRequest = useDecideTeamRequest(id);
  const decidingRequestId = decideTeamRequest.isPending
    ? (decideTeamRequest.variables?.requestId ?? null)
    : null;

  const loiActionError = apiMessage(decide.error) ?? apiMessage(advance.error);

  // The CIM access list holds buyers already in the funnel; a buyer opened from
  // Buyer activity may not be there yet (invited, NDA not signed) — then the
  // detail is built from their activity row: the Teaser stage, nothing unlocked.
  const selectedBuyer: BuyerAccessItem | null = selectedBuyerId
    ? ((buyers.data ?? []).find((b) => b.actorId === selectedBuyerId) ??
      buyerFromEngagement((engagement.data ?? []).find((b) => b.actorId === selectedBuyerId)))
    : null;
  const selectedBuyerGrants = selectedBuyerId
    ? (grants.data ?? []).filter((g) => g.actorId === selectedBuyerId)
    : [];
  const buyerActionError =
    plainError(rollback.error) ??
    plainError(advance.error) ??
    plainError(updateExpiry.error) ??
    plainError(revoke.error);

  const selectBuyer = (actorId: string) => {
    advance.reset();
    rollback.reset();
    updateExpiry.reset();
    revoke.reset();
    setSelectedBuyerId(actorId);
  };

  return (
    <div className="space-y-6">
      <TeamRequestsCard
        requests={teamRequests.data ?? []}
        decidingId={decidingRequestId}
        error={apiMessage(decideTeamRequest.error)}
        onApprove={(requestId, expiresAt) =>
          decideTeamRequest.mutate({ requestId, verdict: 'approve', expiresAt })
        }
        onDecline={(requestId) => decideTeamRequest.mutate({ requestId, verdict: 'decline' })}
      />

      <BuyerPipelineCard
        buyers={engagement.data ?? []}
        lois={lois.data ?? []}
        loading={engagement.isPending}
        error={apiMessage(engagement.error)}
        onSelectBuyer={selectBuyer}
      />

      <CimAccessCard
        buyers={buyers.data ?? []}
        loading={buyers.isPending}
        error={apiMessage(buyers.error)}
        onSelectBuyer={(b) => selectBuyer(b.actorId)}
      />

      <BuyerEngagementCard
        buyers={engagement.data ?? []}
        loading={engagement.isPending}
        error={apiMessage(engagement.error)}
        onSelectBuyer={selectBuyer}
      />

      <LoiManagementCard
        lois={lois.data ?? []}
        loading={lois.isPending}
        error={apiMessage(lois.error)}
        actionError={loiActionError}
        decidingId={decide.isPending ? (decide.variables?.loiId ?? null) : null}
        advancingActorId={advance.isPending ? (advance.variables?.actorId ?? null) : null}
        onDecide={(loiId, decision, terms) => {
          advance.reset();
          decide.mutate({
            loiId,
            body: terms ? { decision, exclusivity: terms } : { decision },
          });
        }}
        onOpen={setViewingLoi}
        onAdvance={(actorId, body) => {
          decide.reset();
          advance.mutate({ actorId, body });
        }}
        onDismissError={() => {
          decide.reset();
          advance.reset();
        }}
        adminMayDecide={room.adminRole === null || room.adminRole === 'intermediary'}
        sellerCanRespondLoi={room.sellerCanRespondLoi ?? true}
        onToggleSellerLoiPermission={(next) =>
          setSellerLoiPermission.mutate({ sellerCanRespondLoi: next })
        }
        sellerLoiPending={setSellerLoiPermission.isPending}
        sellerLoiError={plainError(setSellerLoiPermission.error)}
        exclusivity={exclusivity.data ?? null}
        timezone={room.timezone ?? 'UTC'}
        onExtendExclusivity={(additionalDays) => {
          endExclusivity.reset();
          extend.mutate({ additionalDays });
        }}
        onEndExclusivity={() => {
          extend.reset();
          endExclusivity.mutate();
        }}
        exclusivityPending={extend.isPending || endExclusivity.isPending}
        exclusivityError={plainError(extend.error) ?? plainError(endExclusivity.error)}
        clockToBuyer={room.exclusivityClockToBuyer ?? false}
        clockToOthers={room.exclusivityClockToOthers ?? false}
        onToggleClock={(setting) => clockSettings.mutate(setting)}
        clockPending={clockSettings.isPending}
        clockError={plainError(clockSettings.error)}
      />

      {viewingLoi ? (
        <LoiDocumentDialog
          contentPath={loiDocumentContentPath(id, viewingLoi.id)}
          filename={viewingLoi.filename}
          onClose={() => setViewingLoi(null)}
        />
      ) : null}

      {selectedBuyer ? (
        <BuyerDetailModal
          buyer={selectedBuyer}
          grants={selectedBuyerGrants}
          team={
            <BuyerTeamSection
              team={buyerTeam.data}
              loading={buyerTeam.isPending}
              error={apiMessage(buyerTeam.error)}
              adding={addTeamMember.isPending}
              removingId={
                removeTeamMember.isPending ? (removeTeamMember.variables?.memberId ?? null) : null
              }
              actionError={
                apiMessage(addTeamMember.error) ??
                apiMessage(removeTeamMember.error) ??
                apiMessage(setTeamRoles.error) ??
                apiMessage(decideTeamRequest.error)
              }
              decidingId={decidingRequestId}
              onApprove={(requestId, expiresAt) =>
                decideTeamRequest.mutate({ requestId, verdict: 'approve', expiresAt })
              }
              onDecline={(requestId) => decideTeamRequest.mutate({ requestId, verdict: 'decline' })}
              settingRolesId={
                setTeamRoles.isPending ? (setTeamRoles.variables?.actorId ?? null) : null
              }
              onSetRoles={(memberId, roles) => {
                addTeamMember.reset();
                removeTeamMember.reset();
                setTeamRoles.mutate({ actorId: memberId, roles });
              }}
              onAdd={(email, roles, expiresAt) => {
                removeTeamMember.reset();
                setTeamRoles.reset();
                return addTeamMember.mutateAsync({
                  actorId: selectedBuyer.actorId,
                  email,
                  roles,
                  expiresAt,
                });
              }}
              onRemove={(memberId) => {
                addTeamMember.reset();
                removeTeamMember.mutate({ actorId: selectedBuyer.actorId, memberId });
              }}
            />
          }
          onClose={() => {
            addTeamMember.reset();
            removeTeamMember.reset();
            setSelectedBuyerId(null);
            advance.reset();
            rollback.reset();
            updateExpiry.reset();
            revoke.reset();
          }}
          onAdvance={(actorId, body) => {
            rollback.reset();
            updateExpiry.reset();
            revoke.reset();
            advance.mutate({ actorId, body });
          }}
          onRollback={(actorId) => {
            advance.reset();
            updateExpiry.reset();
            revoke.reset();
            rollback.mutate(actorId);
          }}
          advancing={advance.isPending && advance.variables?.actorId === selectedBuyer.actorId}
          rollingBack={rollback.isPending && rollback.variables === selectedBuyer.actorId}
          onUpdateExpiry={(grantId, body) => {
            advance.reset();
            rollback.reset();
            revoke.reset();
            updateExpiry.mutate({ grantId, body });
          }}
          onRevoke={(grantId) => {
            advance.reset();
            rollback.reset();
            updateExpiry.reset();
            revoke.mutate(grantId);
          }}
          updatingExpiryId={
            updateExpiry.isPending ? (updateExpiry.variables?.grantId ?? null) : null
          }
          revokingId={revoke.isPending ? (revoke.variables ?? null) : null}
          engagement={{
            data: buyerEngagement.data,
            loading: buyerEngagement.isPending,
            error: apiMessage(buyerEngagement.error),
          }}
          actionError={buyerActionError}
          onDismissError={() => {
            advance.reset();
            rollback.reset();
            updateExpiry.reset();
            revoke.reset();
          }}
        />
      ) : null}
    </div>
  );
}
